import { defineStore } from "pinia";
import Cookie from "@/utils/cookie";
import C from "@/utils/constants";
import {
  DEFAULT_RULER_POSITION,
  clampNormalizedPosition,
  resolveEffectiveMpp,
  type EffectiveMpp,
  type NormalizedRulerPosition,
} from "@/utils/measurementRuler";

type TaskRulerState = {
  visible: boolean;
  position: NormalizedRulerPosition;
  renderableSurfaces: Record<string, number>;
  activeSurfaceKey: string | null;
  metadata: EffectiveMpp | null;
  metadataResolved: boolean;
};

type MeasurementRulerState = {
  tasks: Record<string, TaskRulerState>;
  sessionGeneration: number;
};

type PersistedTaskRulerState = Pick<TaskRulerState, "visible" | "position">;

const STORAGE_PREFIX = "annotation2:measurement-ruler:";

const currentSessionScope = () => {
  const user = Cookie.getCookie(C.COOKIES.KEY.USER_ID) || "anonymous";
  const project = (
    Cookie.getCookie(C.COOKIES.KEY.PROJECT_ID)
    || Cookie.getCookie(C.COOKIES.KEY.PROJECT_NAME)
    || "unscoped"
  );
  return `user:${encodeURIComponent(user)}:project:${encodeURIComponent(project)}`;
};
const taskKey = (taskId: string) => (
  `${currentSessionScope()}:task:${encodeURIComponent(taskId)}`
);
const surfaceKey = (surfaceId: string) => `surface:${encodeURIComponent(surfaceId)}`;
const storageKey = (taskId: string) => `${STORAGE_PREFIX}${taskKey(taskId)}`;

function getSessionStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readPersistedTask(taskId: string): PersistedTaskRulerState | null {
  const storage = getSessionStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey(taskId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedTaskRulerState>;
    if (
      typeof parsed.visible !== "boolean"
      || !parsed.position
      || !Number.isFinite(parsed.position.x)
      || !Number.isFinite(parsed.position.y)
    ) return null;
    return {
      visible: parsed.visible,
      position: clampNormalizedPosition(parsed.position),
    };
  } catch {
    return null;
  }
}

function persistTask(taskId: string, task: TaskRulerState) {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(storageKey(taskId), JSON.stringify({
      visible: task.visible,
      position: task.position,
    } satisfies PersistedTaskRulerState));
  } catch {
    // The ruler remains usable in memory when browser storage is unavailable.
  }
}

function clearPersistedTasks() {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) storage.removeItem(key);
    }
  } catch {
    // Session cleanup still clears Pinia even when browser storage is unavailable.
  }
}

const createTaskState = (taskId: string): TaskRulerState => {
  const persisted = readPersistedTask(taskId);
  return {
    visible: persisted?.visible ?? true,
    position: persisted?.position ?? { ...DEFAULT_RULER_POSITION },
    renderableSurfaces: {},
    activeSurfaceKey: null,
    metadata: null,
    metadataResolved: false,
  };
};

function selectFirstRenderableSurface(task: TaskRulerState): string | null {
  return Object.keys(task.renderableSurfaces)[0] ?? null;
}

export const useMeasurementRulerStore = defineStore("measurementRuler", {
  state: (): MeasurementRulerState => ({
    tasks: {},
    sessionGeneration: 0,
  }),
  getters: {
    isVisible: (state) => (taskId: string): boolean =>
      state.tasks[taskKey(taskId)]?.visible ?? readPersistedTask(taskId)?.visible ?? true,
    position: (state) => (taskId: string, _surfaceId?: string): NormalizedRulerPosition => {
      const stored = state.tasks[taskKey(taskId)]?.position ?? readPersistedTask(taskId)?.position;
      return stored ? { ...stored } : { ...DEFAULT_RULER_POSITION };
    },
    metadata: (state) => (taskId: string): EffectiveMpp | null => {
      const stored = state.tasks[taskKey(taskId)]?.metadata;
      return stored ? { ...stored } : null;
    },
    isMetadataResolved: (state) => (taskId: string): boolean =>
      state.tasks[taskKey(taskId)]?.metadataResolved ?? false,
    hasRenderableSurface: (state) => (taskId: string): boolean =>
      Object.keys(state.tasks[taskKey(taskId)]?.renderableSurfaces ?? {}).length > 0,
    isActiveSurface: (state) => (taskId: string, surfaceId: string): boolean =>
      state.tasks[taskKey(taskId)]?.activeSurfaceKey === surfaceKey(surfaceId),
  },
  actions: {
    ensureTask(taskId: string): TaskRulerState {
      const key = taskKey(taskId);
      if (!this.tasks[key]) {
        this.tasks[key] = createTaskState(taskId);
      }
      return this.tasks[key];
    },
    setVisible(taskId: string, visible: boolean) {
      const task = this.ensureTask(taskId);
      task.visible = visible;
      persistTask(taskId, task);
    },
    setPosition(taskId: string, surfaceId: string, position: NormalizedRulerPosition) {
      void surfaceId;
      const task = this.ensureTask(taskId);
      task.position = clampNormalizedPosition(position);
      persistTask(taskId, task);
    },
    registerSurface(taskId: string, surfaceId: string) {
      const task = this.ensureTask(taskId);
      const key = surfaceKey(surfaceId);
      task.renderableSurfaces[key] = (task.renderableSurfaces[key] ?? 0) + 1;
      if (!task.activeSurfaceKey || !task.renderableSurfaces[task.activeSurfaceKey]) {
        task.activeSurfaceKey = key;
      }
    },
    setSurfaceRenderable(taskId: string, surfaceId: string, renderable: boolean) {
      const key = surfaceKey(surfaceId);
      if (renderable) {
        const task = this.ensureTask(taskId);
        task.renderableSurfaces[key] = Math.max(task.renderableSurfaces[key] ?? 0, 1);
        if (!task.activeSurfaceKey || !task.renderableSurfaces[task.activeSurfaceKey]) {
          task.activeSurfaceKey = key;
        }
        return;
      }
      const task = this.tasks[taskKey(taskId)];
      if (!task) return;
      delete task.renderableSurfaces[key];
      if (task.activeSurfaceKey === key) task.activeSurfaceKey = selectFirstRenderableSurface(task);
    },
    activateSurface(taskId: string, surfaceId: string) {
      const task = this.ensureTask(taskId);
      const key = surfaceKey(surfaceId);
      if (task.renderableSurfaces[key]) {
        task.activeSurfaceKey = key;
      }
    },
    unregisterSurface(taskId: string, surfaceId: string) {
      const task = this.tasks[taskKey(taskId)];
      if (!task) return;
      const key = surfaceKey(surfaceId);
      const registrations = task.renderableSurfaces[key] ?? 0;
      if (registrations > 1) {
        task.renderableSurfaces[key] = registrations - 1;
        return;
      }
      delete task.renderableSurfaces[key];
      if (task.activeSurfaceKey === key) task.activeSurfaceKey = selectFirstRenderableSurface(task);
    },
    setMetadata(taskId: string, effectiveMppX: unknown, effectiveMppY: unknown) {
      const task = this.ensureTask(taskId);
      task.metadata = resolveEffectiveMpp(effectiveMppX, effectiveMppY);
      task.metadataResolved = true;
    },
    clearMetadata(taskId: string) {
      const task = this.ensureTask(taskId);
      task.metadata = null;
      task.metadataResolved = false;
      task.renderableSurfaces = {};
      task.activeSurfaceKey = null;
    },
    resetSession() {
      clearPersistedTasks();
      this.tasks = {};
      this.sessionGeneration += 1;
    },
  },
});

export default useMeasurementRulerStore;
